import {groupByUniqueKey} from '@benchling/js-utils';
import Backbone from 'backbone';
import React from 'react';

import AnnotationsTableTabState from 'analyses/AnnotationsTableTabState';
import * as analytics from 'analytics/Analytics';
import {Annotation as BioJSAnnotation, AnnotationAttributes} from 'biojs/Annotation';
import {Sequence} from 'components/Sequence';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {FeaturePanelState} from 'FileStates';
import {getAnnotationData} from 'molbio/annotations/AnnotationUtil';
import Annotation from 'molbio/annotations/backbone/Annotation';
import {Collection as FeatureLibraryCollection} from 'molbio/feature-libraries/FeatureLibrary';
import notifications from 'notifications';
import {Confirm} from 'Popup';
import {getStore} from 'redux-stores/Store';
import AutoAnnotationView from 'toolbar/annotation-panel/AutoAnnotationView';
import {cancelCreateNewAnnotation} from 'toolbar/feature-panel/FeaturePanelUIReduxActions';
import {AnnotationPanelEditData} from 'toolbar/feature-panel/reduceFeaturePanelUI';
import legacyRunModal from 'ui-library/legacyRunModal';
import sortedBy from 'util/sortedBy';

import {AnnotationPanelContentProps} from './AnnotationPanelContent';
import CleanDuplicateAnnotationsModal, {findDuplicateAnnotations} from './CleanDuplicateAnnotationsModal';

const MAX_ANNOTATION_HOVER_COUNT = 100;

export function getAnnotationPanelProps(
  sequence: Sequence,
  panelState: FeaturePanelState
): AnnotationPanelContentProps {
  const annotationsByCid: {[cid: string]: Annotation} = groupByUniqueKey(
    sequence.annotations().models,
    (model) => model.cid
  );
  const {resolvableGroups} = findDuplicateAnnotations(sequence.annotations().models);

  return {
    sortedAnnotationsData: sortedBy(
      sequence.annotations().models.map((annotation) => getAnnotationData(annotation, sequence)),
      ['displayStart', 'displayEnd']
    ),
    canEditSequenceFeatures: sequence.allowFeatureEdits(),
    sequenceId: sequence.id,
    sequenceStartIndex: sequence.startIndex(),
    sequenceLength: sequence.length(),
    isSingleStranded: sequence.isSingleStranded(),
    hasDuplicateAnnotations: resolvableGroups.length > 0,
    onToggleVisibility: (ids, visible) => {
      analytics.track('annotation', 'toggled_visibility', {
        source: 'annotations-panel',
        sequence_id: sequence.id,
        annotation_count: ids.length,
        visible,
      });
      sequence.annotations().batchModify(() => {
        for (const cid of ids) {
          annotationsByCid[cid].set({visible});
        }
      });
    },
    // Syncing hover between the panel and sequence map is sluggish when there are lots of annotations,
    // so only hover in the panel if there are not too many.
    onAnnotationHover:
      sequence.annotations().length > MAX_ANNOTATION_HOVER_COUNT
        ? undefined
        : (id, hover) => {
            annotationsByCid[id].set({hover});
          },
    onSelectAnnotation: (id, selected) => {
      const annotation = annotationsByCid[id];
      if (selected) {
        annotation.click(sequence, false);
        sequence.trigger('jump', annotation.start());
      } else {
        annotation.set('selected', false);
      }
    },
    onSaveAnnotation: (id, data) => {
      const annotation = id != null ? annotationsByCid[id] : null;
      saveAnnotation(annotation, sequence, data);
    },
    onClickAutoAnnotate: () => {
      openAutoAnnotate(sequence);
    },
    onDeleteAnnotation: (id) => {
      annotationsByCid[id].destroy().done(() => {
        sequence.updateVersions();
      });
      analytics.track('annotation', 'deleted_annotation', {source: 'annotations-panel'});
    },
    onOpenCleanDuplicates: () => {
      openDuplicateModal(sequence);
    },
    openExpandedAnnotations: () => {
      let tab = panelState.get('buffers').getTabByClass(AnnotationsTableTabState);
      if (tab != null) {
        tab.set({active: true});
      } else {
        const analysis = new Backbone.Model();
        tab = new AnnotationsTableTabState({analysis, sequence});
        panelState.get('buffers').addTab(tab);
      }
      panelState.trigger('close');
    },
  };
}

export interface SaveAnnotationData extends Omit<AnnotationPanelEditData, 'isCreateActive' | 'shouldFocus'> {}

async function saveAnnotation(
  annotation: Annotation | null,
  sequence: Sequence,
  data: SaveAnnotationData
): Promise<void> {
  const annotationToSave = annotation ?? new Annotation({_annotation: new BioJSAnnotation()});
  const bioAnnotation = annotationToSave.get('_annotation');
  const end = data.end === sequence.length() ? 0 : data.end;
  const {name, notes, annotationType, start, color, strand, customFields} = data;
  if (
    !bioAnnotation.set(
      // These are not actually guaranteed to conform to AnnotationAttributes types, but should get rejected
      // by the validate method if they do not.
      {
        name,
        notes,
        annotation_type: annotationType,
        start,
        end,
        color,
        strand,
        custom_fields: customFields,
      } as Partial<AnnotationAttributes>,
      {validate: true, sequence}
    )
  ) {
    notifications.error(bioAnnotation.validationError);
  } else {
    await sequence.get('_sequence').annotations().betterCreate(bioAnnotation, {
      validate: false,
      fileUpdate: true,
    });
    sequence.updateVersions();
    notifications.success(
      <>
        Annotation <strong>{name}</strong> saved successfully.
      </>
    );

    // If the annotation was successfully created/saved, close the edit form
    if (annotation == null) {
      getStore().dispatch(cancelCreateNewAnnotation(sequence.id));
    } else {
      annotation.set('selected', false);
    }
  }

  if (annotation != null) {
    analytics.track('annotation', 'updated_annotations', {
      source: 'annotations-panel',
      sequence_api_id: sequence.id,
      annotation_count: 1,
    });
  } else {
    analytics.track('annotation', 'created_annotation', {
      strand,
      color,
      sequence_id: sequence.id,
      product: ANALYTICS_MODULE.MOLBIO,
    });
  }
}

export function openAutoAnnotate(sequence: Sequence): void {
  const libraries = new FeatureLibraryCollection();
  const autoAnnotationView = new AutoAnnotationView({
    model: libraries,
    // @ts-expect-error
    sequence,
  });
  const p = new Confirm({title: 'Auto-annotate sequence', msg: autoAnnotationView.render().el}).choice(
    'Add annotations',
    'button button--medium button--primary',
    () => {
      autoAnnotationView.addAnnotationsToSequence();
    },
    true,
    {component: 'AnnotationPanelUtil', element: 'addAnnotationsButton'}
  );
  p.show();
  // Disable go button. Can't do in render because it's not yet attached.
  p.$el.find('.modal-footer button').addClass('disabled');
  analytics.track('annotation', 'clicked_auto_annotate', {
    product: ANALYTICS_MODULE.MOLBIO,
    sequence_id: sequence.get('id'),
  });
}

export async function openDuplicateModal(sequence: Sequence): Promise<void> {
  const {resolvableGroups, numUnresolvableGroups} = findDuplicateAnnotations(sequence.annotations().models);
  const idsToDelete = await legacyRunModal(CleanDuplicateAnnotationsModal, {
    resolvableGroups,
    numUnresolvableGroups,
  });
  if (idsToDelete != null) {
    cleanDuplicates(sequence, idsToDelete);
  }
}

async function cleanDuplicates(sequence: Sequence, idsToDelete: Array<number>): Promise<void> {
  const modelsToDelete = [];
  for (const annotation of sequence.annotations().models) {
    if (idsToDelete.includes(annotation.id)) {
      modelsToDelete.push(annotation.get('_annotation'));
    }
  }
  try {
    const res = await sequence.annotations()._annotations.bulkDelete(modelsToDelete, {fileUpdate: true});
    sequence.updateVersions();
    notifications.success(res!.message);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
  }
}
